import type { TFunction } from 'i18next'
import { z } from 'zod'

import type { Contact } from '@features/contacts/types/contact'
import type { ContactPerson } from '@features/contacts/types/contactPerson'
import { ContactType } from '@features/contacts/types/contactType'

import { isValidEmail } from '../../../../../../../../../utils'
import type { AdditionalContactPersonFormValues, SupplierFormSchema, SupplierFormValues } from '../types/supplierForm'

// Shared by the individual's own email and each additional contact person: optional, but a filled
// value must be a real email address.
const getContactPersonEmailSchema = (t: TFunction) =>
  z.string().refine((email) => !email.trim() || isValidEmail(email.trim()), {
    message: t('suppliers.detail.form.validation.invalid_email'),
  })

// Fields both tabs save, regardless of which one is on screen.
const getSharedSupplierFieldsSchema = (t: TFunction) => ({
  additionalContactPersons: z.array(
    z.object({
      contactPersonId: z.string().optional(),
      email: getContactPersonEmailSchema(t),
      firstName: z.string(),
      lastName: z.string(),
    }),
  ),
  cityText: z.string(),
  defaultExpenseAccountId: z.string(),
  defaultExpenseProductDescription: z.string(),
  defaultTaxRateId: z.string(),
  phone: z.string(),
  registrationNo: z.string(),
  stateText: z.string(),
  street: z.string(),
  vatNo: z.string(),
  zipcodeText: z.string(),
})

// The company tab has no country field of its own - a registry search fills it in, but a manually
// entered company can still be saved without one.
const getCompanySupplierSchema = (t: TFunction) =>
  z.object({
    ...getSharedSupplierFieldsSchema(t),
    countryId: z.string(),
    email: getContactPersonEmailSchema(t),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string().trim().min(1, t('suppliers.detail.form.validation.company_name_required')),
    type: z.literal(ContactType.Company),
  })

const getPersonSupplierSchema = (t: TFunction) =>
  z.object({
    ...getSharedSupplierFieldsSchema(t),
    countryId: z.string().trim().min(1, t('suppliers.detail.form.validation.country_required')),
    email: getContactPersonEmailSchema(t),
    firstName: z.string().trim().min(1, t('suppliers.detail.form.validation.first_name_required')),
    lastName: z.string().trim().min(1, t('suppliers.detail.form.validation.last_name_required')),
    name: z.string(),
    type: z.literal(ContactType.Person),
  })

// A schema per tab, picked by `type`: each tab's own fields are required there and unconstrained on
// the other, so switching tabs without filling them in cannot block a save for fields that tab
// doesn't show, and the other tab's values are never validated.
export const getSupplierFormSchema = (t: TFunction): SupplierFormSchema =>
  z.discriminatedUnion('type', [getCompanySupplierSchema(t), getPersonSupplierSchema(t)])

export const getEmptyAdditionalContactPerson = (): AdditionalContactPersonFormValues => ({
  email: '',
  firstName: '',
  lastName: '',
})

export const getSupplierContactType = (contact: Partial<Contact> | null): ContactType =>
  contact?.type === ContactType.Person ? ContactType.Person : ContactType.Company

// A supplier can have people with no primary flag. The individual view still treats the first one as its
// primary person, so that person is edited through the individual fields and not listed as additional.
export const getPrimaryContactPerson = (contactPersons: ContactPerson[]): ContactPerson | undefined =>
  contactPersons.find((contactPerson) => contactPerson.isPrimary) ?? contactPersons[0]

export const getAdditionalContactPersons = (contactPersons: ContactPerson[], type: ContactType): ContactPerson[] => {
  const primaryContactPerson = type === ContactType.Person ? getPrimaryContactPerson(contactPersons) : undefined

  return contactPersons.filter((contactPerson) => contactPerson !== primaryContactPerson && !contactPerson.isPrimary)
}

export const getSupplierFormDefaultValues = (
  contact: Partial<Contact> | null,
  type: ContactType = getSupplierContactType(contact),
  // Only meant to fall back to the organization's own country when creating a brand-new supplier -
  // callers editing an existing contact should leave this unset so a contact saved without a
  // country stays that way instead of silently inheriting the organization's.
  defaultCountryId?: string,
): SupplierFormValues => {
  const contactPersons = contact?.contactPersons ?? []
  const primaryContactPerson = getPrimaryContactPerson(contactPersons)
  const additionalContactPersons = getAdditionalContactPersons(contactPersons, type)

  return {
    additionalContactPersons: additionalContactPersons.length
      ? additionalContactPersons.map((contactPerson) => ({
          contactPersonId: contactPerson.id,
          email: contactPerson.email ?? '',
          firstName: contactPerson.firstName ?? '',
          lastName: contactPerson.lastName ?? '',
        }))
      : [getEmptyAdditionalContactPerson()],
    cityText: contact?.cityText ?? '',
    countryId: contact?.countryId ?? defaultCountryId ?? '',
    defaultExpenseAccountId: contact?.defaultExpenseAccountId ?? '',
    defaultExpenseProductDescription: contact?.defaultExpenseProductDescription ?? '',
    defaultTaxRateId: contact?.defaultTaxRateId ?? '',
    email: primaryContactPerson?.email ?? contact?.email ?? '',
    firstName: primaryContactPerson?.firstName ?? '',
    lastName: primaryContactPerson?.lastName ?? '',
    name: contact?.name ?? '',
    phone: contact?.phone ?? '',
    registrationNo: contact?.registrationNo ?? '',
    stateText: contact?.stateText ?? '',
    street: contact?.street ?? '',
    type,
    vatNo: contact?.vatNo ?? '',
    zipcodeText: contact?.zipcodeText ?? '',
  }
}
